import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { format, parseISO, addDays } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  CalendarDays,
  ClipboardList,
  Download,
  Eye,
  Filter,
  Image as ImageIcon,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Repeat,
  RefreshCcw,
  Search,
  Share2,
  X
} from 'lucide-react'
import FeaturedLayout from '../layouts/FeaturedLayout'
import ProgramaEditorModal from './ProgramaEditorModal'
import AsignacionModal from './AsignacionModal'
import ProgramaTiposBar from './ProgramaTiposBar'
import { useAuth } from '../../hooks/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { getMexicoDateString } from '../../lib/dateUtils'
import {
  getAllGroups,
  getStaffGroups,
  getTrainingProgramAgenda,
  getTrainingProgramsLibrary,
  generateTrainingFileUrl
} from '../../lib/supabase'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui'

const getToday = () => getMexicoDateString()
const getDefaultEndDate = () => getMexicoDateString(addDays(new Date(), 7))

const buildFileKey = (file) => file?.id || file?.storage_path || file?.file_name

const isImageFile = (file) => {
  const mime = file?.file_type?.toLowerCase() || ''
  if (mime.startsWith('image/')) return true
  const name = file?.file_name?.toLowerCase() || ''
  return ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some((ext) => name.endsWith(ext))
}

const TrainingProgramsPage = ({ view = 'agenda' }) => {
  const { staff } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { canManagePrograms } = usePermissions()
  const canAccessAllGroups = canManagePrograms
  const isAgendaView = view === 'agenda'
  const layoutSubtitle = isAgendaView
    ? 'Consulta y administra los programas asignados por día y grupo'
    : 'Gestiona los materiales oficiales y reutiliza programas para nuevos días'

  const [filters, setFilters] = useState({
    startDate: getToday(),
    endDate: getDefaultEndDate(),
    groupId: 'todos',
    search: ''
  })
  const [agendaItems, setAgendaItems] = useState([])
  const [agendaLoading, setAgendaLoading] = useState(false)
  const [agendaError, setAgendaError] = useState(null)
  const [showFilters, setShowFilters] = useState(false)

  const [libraryItems, setLibraryItems] = useState([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const [libraryError, setLibraryError] = useState(null)

  const [groups, setGroups] = useState([])
  const [accessibleGroupIds, setAccessibleGroupIds] = useState([])

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [asignacionOpen, setAsignacionOpen] = useState(false)
  const [programaAsignar, setProgramaAsignar] = useState(null)
  const [editingProgram, setEditingProgram] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [filePreviewUrls, setFilePreviewUrls] = useState({})
  const [brokenThumbs, setBrokenThumbs] = useState({})

  const groupedAgenda = useMemo(() => {
    if (!agendaItems.length) return []

    const groupsMap = agendaItems.reduce((acc, item) => {
      const key = item.fecha || 'sin-fecha'
      if (!acc[key]) acc[key] = []
      acc[key].push(item)
      return acc
    }, {})

    return Object.entries(groupsMap).sort((a, b) => new Date(a[0]) - new Date(b[0]))
  }, [agendaItems])

  const filteredLibraryItems = useMemo(() => {
    if (!filters.search) return libraryItems
    const term = filters.search.toLowerCase()
    return libraryItems.filter((programa) => {
      const title = programa.titulo?.toLowerCase() || ''
      const desc = programa.descripcion?.toLowerCase() || ''
      const tags = (programa.tags || []).join(' ').toLowerCase()
      return title.includes(term) || desc.includes(term) || tags.includes(term)
    })
  }, [libraryItems, filters.search])

  useEffect(() => {
    const initialGroup = searchParams.get('groupId')
    const requestedDate = searchParams.get('date')
    setFilters((prev) => ({
      ...prev,
      groupId: initialGroup || prev.groupId,
      startDate: requestedDate || prev.startDate,
      endDate: requestedDate || prev.endDate
    }))
  }, [searchParams])

  useEffect(() => {
    const loadGroups = async () => {
      try {
        if (canAccessAllGroups) {
          const { data, error } = await getAllGroups()
          if (error) throw error
          setGroups(data || [])
          setAccessibleGroupIds([])
        } else if (staff?.id) {
          const { data, error } = await getStaffGroups(staff.id)
          if (error) throw error
          const mapped = (data || [])
            .map((item) => item.grupos)
            .filter(Boolean)
          setGroups(mapped)
          setAccessibleGroupIds(mapped.map((group) => group.id))
        } else {
          setGroups([])
          setAccessibleGroupIds([])
        }
      } catch (error) {
        console.error('Error loading groups:', error)
      }
    }

    loadGroups()
  }, [canAccessAllGroups, staff?.id])

  useEffect(() => {
    if (!isAgendaView) return
    loadAgenda()
  }, [
    isAgendaView,
    canAccessAllGroups,
    filters.startDate,
    filters.endDate,
    filters.groupId,
    filters.search,
    accessibleGroupIds.join('|')
  ])

  useEffect(() => {
    if (canManagePrograms) {
      loadLibrary()
    }
  }, [canManagePrograms])

  useEffect(() => {
    const fromAgenda = (agendaItems || [])
      .flatMap((item) => item.programa?.archivos || [])
      .filter(isImageFile)
    const fromLibrary = (libraryItems || [])
      .flatMap((programa) => programa.archivos || [])
      .filter(isImageFile)

    const missingFiles = [...fromAgenda, ...fromLibrary].filter((file) => {
      const key = buildFileKey(file)
      return key && !filePreviewUrls[key] && !brokenThumbs[key]
    })

    if (missingFiles.length) {
      prefetchImageThumbs(missingFiles).catch((error) =>
        console.error('Error precargando miniaturas:', error)
      )
    }
  }, [agendaItems, libraryItems, filePreviewUrls, brokenThumbs])

  const handleFiltersChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }))
  }


  const getSignedUrlForFile = async (file, expiresIn = 3600) => {
    if (!file) throw new Error('Archivo inválido')
    if (file.storage_path) {
      const { data, error } = await generateTrainingFileUrl(file.storage_path, expiresIn)
      if (error) throw error
      return data?.signedUrl
    }
    if (file.file_url) {
      return file.file_url
    }
    throw new Error('No pudimos obtener el archivo')
  }

  const prefetchImageThumbs = async (files = []) => {
    if (!files.length) return

    const tasks = []
    const seenKeys = new Set()

    files.forEach((file) => {
      if (!isImageFile(file)) return
      const key = buildFileKey(file)
      if (!key || seenKeys.has(key) || filePreviewUrls[key] || brokenThumbs[key]) return
      seenKeys.add(key)
      tasks.push({ key, file })
    })

    if (!tasks.length) return

    const results = await Promise.all(
      tasks.map(async ({ key, file }) => {
        try {
          const url = await getSignedUrlForFile(file)
          return { key, url }
        } catch (error) {
          console.error('Error obteniendo miniatura:', error)
          return { key, url: null }
        }
      })
    )

    const resolved = {}
    const failed = {}

    results.forEach(({ key, url }) => {
      if (url) {
        resolved[key] = url
      } else {
        failed[key] = true
      }
    })

    if (Object.keys(resolved).length) {
      setFilePreviewUrls((prev) => ({ ...prev, ...resolved }))
    }

    if (Object.keys(failed).length) {
      setBrokenThumbs((prev) => ({ ...prev, ...failed }))
    }
  }

  const getThumbUrl = (file) => {
    const key = buildFileKey(file)
    if (!key) return file?.file_url || null
    if (filePreviewUrls[key]) return filePreviewUrls[key]
    if (brokenThumbs[key]) return null
    return file?.file_url || null
  }

  const handleThumbError = (file) => {
    const key = buildFileKey(file)
    if (!key) return
    setBrokenThumbs((prev) => ({ ...prev, [key]: true }))
    setFilePreviewUrls((prev) => {
      if (!prev[key]) return prev
      const copy = { ...prev }
      delete copy[key]
      return copy
    })
  }

  const openCreateModal = () => {
    setEditingProgram(null)
    setCreateModalOpen(true)
  }

  const canEditProgram = (programa) => {
    if (!programa) return false
    if (canManagePrograms) return true
    if (!staff?.id) return false
    return programa.created_by && programa.created_by === staff.id
  }

  const loadAgenda = async () => {
    setAgendaLoading(true)
    setAgendaError(null)
    try {
      const groupFilter = filters.groupId !== 'todos' ? filters.groupId : null
      const { data, error } = await getTrainingProgramAgenda({
        startDate: filters.startDate,
        endDate: filters.endDate,
        groupId: groupFilter
      })
      if (error) throw error
      let filtered = data || []

      if (!canAccessAllGroups && accessibleGroupIds.length) {
        filtered = filtered.filter(
          (item) => !item.grupo_id || accessibleGroupIds.includes(item.grupo_id)
        )
      }

      if (filters.search) {
        const term = filters.search.toLowerCase()
        filtered = filtered.filter((item) => {
          const title = item.programa?.titulo?.toLowerCase() || ''
          const desc = item.programa?.descripcion?.toLowerCase() || ''
          const group = item.grupo?.nombre_del_grupo?.toLowerCase() || ''
          const tags = (item.programa?.tags || []).join(' ').toLowerCase()
          return (
            title.includes(term) ||
            desc.includes(term) ||
            group.includes(term) ||
            tags.includes(term)
          )
        })
      }

      setAgendaItems(filtered)
      await prefetchImageThumbs(
        filtered.flatMap((item) => item.programa?.archivos || [])
      )
    } catch (error) {
      console.error('Error loading training agenda:', error)
      setAgendaError('No pudimos cargar los programas asignados')
    } finally {
      setAgendaLoading(false)
    }
  }

  const loadLibrary = async () => {
    if (!canManagePrograms) return
    setLibraryLoading(true)
    setLibraryError(null)
    try {
      const { data, error } = await getTrainingProgramsLibrary()
      if (error) throw error
      const libraryData = data || []
      setLibraryItems(libraryData)
      await prefetchImageThumbs(
        libraryData.flatMap((programa) => programa.archivos || [])
      )
    } catch (error) {
      console.error('Error loading library:', error)
      setLibraryError('No pudimos cargar la biblioteca de programas')
    } finally {
      setLibraryLoading(false)
    }
  }

  const closePreviewModal = () => {
    setPreviewFile(null)
    setPreviewUrl(null)
    setPreviewLoading(false)
  }

  const handleDownloadFile = async (file) => {
    try {
      const signedUrl = await getSignedUrlForFile(file)
      if (signedUrl) window.open(signedUrl, '_blank')
    } catch (error) {
      console.error('Error abriendo archivo:', error)
      setAgendaError('No pudimos obtener el archivo. Intenta nuevamente.')
    }
  }

  const handlePreviewFile = async (file) => {
    try {
      setPreviewLoading(true)
      setPreviewFile(file)
      const signedUrl = await getSignedUrlForFile(file)
      setPreviewUrl(signedUrl)
      const key = buildFileKey(file)
      if (signedUrl && key) {
        setFilePreviewUrls((prev) => ({ ...prev, [key]: signedUrl }))
      }
    } catch (error) {
      console.error('Error generando vista previa:', error)
      setAgendaError('No pudimos cargar la vista previa.')
      setPreviewFile(null)
      setPreviewUrl(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const openEditModal = (programa) => {
    if (!programa) return
    setEditingProgram(programa)
    setCreateModalOpen(true)
  }

  const openReuseModal = async (programa) => {
    try {
      const { getProgramaSecciones } = await import('../../lib/supabase')
      const { data } = await getProgramaSecciones(programa.id)
      setProgramaAsignar({ ...programa, secciones: data || [] })
    } catch {
      setProgramaAsignar(programa)
    }
    setAsignacionOpen(true)
  }

  const renderAgenda = () => {
    if (agendaLoading) {
      return (
        <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Cargando programas...
        </div>
      )
    }

    if (agendaError) {
      return (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{agendaError}</AlertDescription>
        </Alert>
      )
    }

    const todayKey = getToday()
    const todayItems = agendaItems.filter((item) => item.fecha === todayKey)
    const todayPrograms = todayItems.reduce((acc, entry) => {
      const programId = entry.programa?.id || entry.programa_id || entry.id
      if (!programId) return acc
      if (!acc[programId]) {
        acc[programId] = {
          programa: entry.programa,
          registros: []
        }
      }
      acc[programId].registros.push(entry)
      return acc
    }, {})

    return (
      <div className="space-y-6">
        <Card className="border shadow-none">
          <CardHeader className="pb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base font-semibold">Programa de hoy</CardTitle>
            </div>
            <Badge variant="secondary">
              {format(parseISO(todayKey), "d 'de' MMMM", { locale: es })}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.keys(todayPrograms).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No hay programas asignados para hoy.
              </p>
            ) : (
              Object.values(todayPrograms).map(({ programa, registros }) => {
                const primaryFile = programa?.archivos?.[0]
                const groupNames = registros
                  .map((registro) => registro.grupo?.nombre_del_grupo)
                  .filter(Boolean)
                const uniqueGroups = Array.from(new Set(groupNames))
                const hasGenericAssignment = registros.some(
                  (registro) => !registro.grupo?.nombre_del_grupo
                )
                return (
                  <div
                    key={programa?.id || registros[0]?.id}
                    className="flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {programa?.titulo || 'Programa sin título'}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        {uniqueGroups.map((group) => (
                          <Badge key={group} variant="outline" className="text-[11px]">
                            {group}
                          </Badge>
                        ))}
                        {hasGenericAssignment && (
                          <Badge variant="secondary" className="text-[11px]">
                            General
                          </Badge>
                        )}
                      </div>
                    </div>
                    {primaryFile ? (
                      <Button
                        size="sm"
                        className="sm:ml-4"
                        onClick={() => handlePreviewFile(primaryFile)}
                      >
                        Abrir
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled className="sm:ml-4">
                        Sin archivo
                      </Button>
                    )}
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Programas por fecha</h3>
          <p className="text-xs text-muted-foreground">
            {groupedAgenda.length} día(s)
          </p>
        </div>
        {!agendaItems.length && (
          <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
            No hay programas asignados en el periodo seleccionado.
            <div className="mt-2 text-xs text-muted-foreground">
              Ajusta las fechas o crea un nuevo programa.
            </div>
          </div>
        )}
        {groupedAgenda.map(([dateKey, items]) => {
          const readableDate =
            dateKey === 'sin-fecha'
              ? 'Sin fecha definida'
              : format(parseISO(dateKey), "EEEE d 'de' MMMM", { locale: es })
          const programsById = items.reduce((acc, entry) => {
            const programId = entry.programa?.id || entry.programa_id || entry.id
            if (!programId) return acc
            if (!acc[programId]) {
              acc[programId] = {
                programa: entry.programa,
                registros: []
              }
            }
            acc[programId].registros.push(entry)
            return acc
          }, {})
          return (
            <Card key={dateKey}>
              <CardHeader className="pb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base font-semibold">
                    {readableDate}
                  </CardTitle>
                </div>
                <Badge variant="secondary">
                  {Object.keys(programsById).length} programa(s)
                </Badge>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.values(programsById).map(({ programa, registros }) => {
                  const primaryFile = programa?.archivos?.[0]
                  const thumbUrl = primaryFile ? getThumbUrl(primaryFile) : null
                  const handlePrimaryPreview = () => {
                    if (primaryFile) handlePreviewFile(primaryFile)
                  }
                  const groupNames = registros
                    .map((registro) => registro.grupo?.nombre_del_grupo)
                    .filter(Boolean)
                  const uniqueGroups = Array.from(new Set(groupNames))
                  const hasGenericAssignment = registros.some(
                    (registro) => !registro.grupo?.nombre_del_grupo
                  )

                  return (
                    <div
                      key={programa?.id || registros[0]?.id}
                      className="rounded-lg border border-border p-4 hover:border-border"
                    >
                      <div className="flex flex-col gap-4 md:flex-row">
                        <div
                          className={`relative w-full md:w-44 lg:w-52 aspect-[4/3] rounded-xl bg-muted overflow-hidden flex items-center justify-center ${
                            primaryFile ? 'cursor-pointer' : ''
                          }`}
                          onClick={primaryFile ? handlePrimaryPreview : undefined}
                        >
                          {thumbUrl ? (
                            <img
                              src={thumbUrl}
                              alt="Vista previa del programa"
                              className="h-full w-full object-cover transition-transform duration-200 hover:scale-[1.02]"
                              onError={(e) => {
                                e.currentTarget.onerror = null
                                handleThumbError(primaryFile)
                              }}
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center py-6 text-xs text-muted-foreground">
                              <ImageIcon className="h-6 w-6 mb-2 text-muted-foreground" />
                              Vista previa no disponible
                            </div>
                          )}
                          {primaryFile && (
                            <Button
                              size="icon"
                              variant="secondary"
                              className="absolute top-2 right-2 bg-card/80 hover:bg-card"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDownloadFile(primaryFile)
                              }}
                            >
                              <Download className="h-4 w-4 text-foreground" />
                            </Button>
                          )}
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="flex flex-wrap items-center gap-2 justify-between">
                            <div className="flex-1 min-w-[180px]">
                              <p className="text-sm font-semibold text-foreground">
                                {programa?.titulo || 'Programa sin título'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {uniqueGroups.length
                                  ? `${uniqueGroups.length} grupo(s) asignado(s)`
                                  : hasGenericAssignment
                                  ? 'Todos los grupos'
                                  : 'Sin grupo definido'}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(programa?.deportes || []).map((sport) => (
                              <Badge key={sport} variant="outline">
                                {sport}
                              </Badge>
                            ))}
                            {(programa?.tags || []).slice(0, 2).map((tag) => (
                              <Badge key={tag} variant="secondary">
                                {tag}
                              </Badge>
                            ))}
                            {programa?.tags?.length > 2 && (
                              <Badge variant="secondary">
                                +{programa.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                          {programa?.descripcion && (
                            <p className="text-sm text-muted-foreground">{programa.descripcion}</p>
                          )}
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="font-semibold text-muted-foreground">Grupos:</span>
                            {uniqueGroups.length ? (
                              uniqueGroups.map((name) => (
                                <Badge key={name} variant="outline" className="text-[11px]">
                                  {name}
                                </Badge>
                              ))
                            ) : hasGenericAssignment ? (
                              <Badge variant="outline" className="text-[11px]">
                                Todos los grupos
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[11px]">
                                Sin grupo
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 pt-2">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/programas/${programa.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver detalle
                            </Button>
                            {primaryFile && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handlePrimaryPreview}
                              >
                                Abrir material
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" aria-label="Mas acciones">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEditProgram(programa) && (
                                  <DropdownMenuItem onClick={() => openEditModal(programa)}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Editar
                                  </DropdownMenuItem>
                                )}
                                {canManagePrograms && (
                                  <DropdownMenuItem onClick={() => openReuseModal(programa)}>
                                    <Repeat className="h-4 w-4 mr-2" />
                                    Usar otro dia
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => navigate(`/programas/${programa.id}/imprimible`)}
                                >
                                  <Share2 className="h-4 w-4 mr-2" />
                                  Compartir / Imprimir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          )
        })}
      </div>
    )
  }

  const renderLibrary = () => {
    if (!canManagePrograms) {
      return (
        <div className="text-sm text-muted-foreground py-10 text-center">
          Solo administradores o head coach pueden administrar la biblioteca de programas.
        </div>
      )
    }

    if (libraryLoading) {
      return (
        <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Cargando biblioteca...
        </div>
      )
    }

    if (libraryError) {
      return (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{libraryError}</AlertDescription>
        </Alert>
      )
    }

    if (!filteredLibraryItems.length) {
      return (
        <div className="text-center py-12 text-sm text-muted-foreground">
          Aún no hay programas guardados en la biblioteca.
        </div>
      )
    }

    return (
      <div className="grid gap-4 md:grid-cols-2">
        {filteredLibraryItems.map((programa) => {
          const primaryFile = programa.archivos?.[0]
          const thumbUrl = primaryFile ? getThumbUrl(primaryFile) : null
          const handleLibraryPreview = () => {
            if (primaryFile) handlePreviewFile(primaryFile)
          }
          return (
            <Card key={programa.id}>
              <CardContent className="space-y-3 p-4">
                {/* Barra de tipos de seccion (DNA visual del programa) */}
                {programa.secciones && programa.secciones.length > 0 && (
                  <ProgramaTiposBar secciones={programa.secciones} />
                )}
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold text-foreground">{programa.titulo}</p>
                      {programa.secciones && programa.secciones.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          ⏱ {Math.floor(programa.secciones.reduce((s, x) => s + (x.duracion_seg || 0), 0) / 60)} min · {programa.secciones.length} secciones
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {format(parseISO(programa.created_at), "d 'de' MMMM yyyy", { locale: es })}
                      </p>
                    </div>
                  <div className="flex flex-wrap gap-2">
                    {(programa.deportes || []).map((sport) => (
                      <Badge key={sport} variant="outline">
                        {sport}
                      </Badge>
                    ))}
                    {Array.isArray(programa.habilidades_relacion) && programa.habilidades_relacion.map((rel) => (
                      rel.deporte_habilidades && (
                        <Badge key={rel.habilidad_id} variant="outline" className="border-blue-200 text-blue-700">
                          {rel.deporte_habilidades.nombre}
                        </Badge>
                      )
                    ))}
                    {(programa.tags || []).map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  {programa.descripcion && (
                    <p className="text-sm text-muted-foreground">{programa.descripcion}</p>
                  )}
                  {Array.isArray(programa.agenda) && programa.agenda.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      <p className="font-medium text-muted-foreground">Fechas asignadas:</p>
                      <ul className="mt-1 list-disc pl-4 space-y-0.5">
                        {programa.agenda.slice(0, 3).map((registro) => (
                          <li key={registro.id}>
                            {format(parseISO(registro.fecha), "EEE d 'de' MMM", { locale: es })}
                            {registro.grupo?.nombre_del_grupo && (
                              <span className="text-muted-foreground"> · {registro.grupo.nombre_del_grupo}</span>
                            )}
                          </li>
                        ))}
                        {programa.agenda.length > 3 && (
                          <li className="text-muted-foreground">+{programa.agenda.length - 3} más</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {/* Acciones */}
                  <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/programas/${programa.id}`)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Ver detalle
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" aria-label="Mas acciones">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEditProgram(programa) && (
                        <DropdownMenuItem onClick={() => openEditModal(programa)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => openReuseModal(programa)}>
                        <Repeat className="h-4 w-4 mr-2" />
                        Asignar a otro dia
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate(`/programas/${programa.id}/imprimible`)}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Compartir / Imprimir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    )
  }

  const renderAgendaFilters = () => (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters((prev) => !prev)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filtros
          </Button>
          <Badge variant="secondary">
            {format(parseISO(filters.startDate), 'dd/MM/yyyy')} - {format(parseISO(filters.endDate), 'dd/MM/yyyy')}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManagePrograms && (
            <Button onClick={openCreateModal} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Nuevo programa
            </Button>
          )}
          {canManagePrograms && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate('/programas/biblioteca')}
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              Ir a biblioteca
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadAgenda} disabled={agendaLoading} title="Actualizar">
            <RefreshCcw className={`h-4 w-4 ${agendaLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      {showFilters && (
        <Card className="border shadow-none">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label htmlFor="startDate">Desde</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFiltersChange('startDate', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="endDate">Hasta</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFiltersChange('endDate', e.target.value)}
                />
              </div>
              <div>
                <Label>Grupo</Label>
                <Select value={filters.groupId} onValueChange={(v) => handleFiltersChange('groupId', v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Todos los grupos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los grupos</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.nombre_del_grupo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Buscar</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={filters.search}
                    onChange={(e) => handleFiltersChange('search', e.target.value)}
                    placeholder="Nombre, descripción..."
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )

  const renderLibraryFilters = () => (
    <div className="flex items-center gap-2 flex-nowrap">
      <div className="flex-1 min-w-0 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => handleFiltersChange('search', e.target.value)}
          placeholder="Buscar por nombre o descripción..."
          className="pl-10"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
          <span className="font-medium text-foreground">{filteredLibraryItems.length}</span>{' '}
          {filteredLibraryItems.length === 1 ? 'programa' : 'programas'}
        </span>
        <Button
          variant="outline"
          onClick={() => navigate('/programas/agenda')}
          className="h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3"
          aria-label="Ver agenda"
          title="Ver agenda"
        >
          <CalendarDays className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Ver agenda</span>
        </Button>
        {canManagePrograms && (
          <Button onClick={openCreateModal} className="h-10" aria-label="Nuevo programa" title="Nuevo programa">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Nuevo programa</span>
          </Button>
        )}
      </div>
    </div>
  )


  const renderPreviewModal = () => (
    <Dialog
      open={Boolean(previewFile)}
      onOpenChange={(open) => {
        if (!open) closePreviewModal()
      }}
    >
      <DialogContent className="w-full max-w-none h-screen bg-black/95 p-0 flex flex-col">
        <DialogHeader className="sr-only">
          <DialogTitle>Vista previa del programa</DialogTitle>
          <DialogDescription>
            {previewFile?.file_name
              ? `Mostrando el archivo ${previewFile.file_name}`
              : 'Vista previa del archivo del programa de entrenamiento'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end p-4">
          <Button variant="ghost" className="text-white hover:bg-card/10" onClick={closePreviewModal}>
            <X className="mr-2 h-4 w-4" />
            Cerrar
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 pb-6 overflow-hidden">
          {previewLoading && (
            <div className="flex items-center gap-2 text-sm text-white/80">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando archivo...
            </div>
          )}
          {!previewLoading && previewUrl && previewFile?.file_type?.startsWith('image/') && (
            <img
              src={previewUrl}
              alt={previewFile.file_name}
              className="max-h-full max-w-full object-contain"
            />
          )}
          {!previewLoading && previewUrl && !previewFile?.file_type?.startsWith('image/') && (
            <iframe
              title={`preview-${previewFile?.file_name}`}
              src={previewUrl}
              className="w-full h-full rounded-md bg-card"
            />
          )}
          {!previewLoading && !previewUrl && (
            <p className="text-sm text-white/80">No pudimos mostrar una vista previa.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )

  return (
    <FeaturedLayout
      title={isAgendaView ? 'Programas de entrenamiento' : 'Biblioteca de programas'}
      subtitle={layoutSubtitle}
      refreshAction={isAgendaView ? loadAgenda : loadLibrary}
    >
      {isAgendaView ? renderAgendaFilters() : renderLibraryFilters()}
      <div className="mt-4">
        {isAgendaView ? renderAgenda() : renderLibrary()}
      </div>
      <ProgramaEditorModal
        open={createModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreateModalOpen(false)
            setEditingProgram(null)
          }
        }}
        programaId={editingProgram?.id || null}
        onSaved={async () => {
          setCreateModalOpen(false)
          setEditingProgram(null)
          await loadAgenda()
          if (canManagePrograms) {
            await loadLibrary()
          }
        }}
      />
      <AsignacionModal
        open={asignacionOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAsignacionOpen(false)
            setProgramaAsignar(null)
          }
        }}
        programa={programaAsignar}
        onAssigned={async () => {
          await loadAgenda()
          if (canManagePrograms) await loadLibrary()
        }}
      />
      {renderPreviewModal()}
    </FeaturedLayout>
  )
}

export default TrainingProgramsPage




